import { GoToMealsRequest } from "@/clients/models/GoToMealsRequest";
import { MealTime } from "@/clients/models/MealTime";
import { QueryKeys } from "@/constants/query-keys";
import { useGenerateCandidate } from "@/hooks/requests/go-to/use-generate-candidate";
import { useGetGotoPreference } from "@/hooks/requests/go-to/use-get-goto-preference";
import { useToast } from "@/hooks/ui/use-toast";
import { GoToFoodFormData, GoToFoodSchema } from "@/schemas/go-to-food-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

interface UseFavouriteFoodProfileProps {
  mealTime: MealTime;
  onSuccess: () => void;
}

export default function useFavouriteFoodProfile({
  mealTime,
  onSuccess,
}: UseFavouriteFoodProfileProps) {
  const queryClient = useQueryClient();
  const { gotoPreference, isLoading } = useGetGotoPreference(mealTime);
  const { generateCandidate, isGenerating } = useGenerateCandidate(mealTime);
  const { showError } = useToast();

  const form = useForm<GoToFoodFormData>({
    resolver: zodResolver(GoToFoodSchema),
    defaultValues: {
      selectedMeals: [],
    },
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = form;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (gotoPreference?.preferences) {
      const mealNames = gotoPreference.preferences.map((meal) => ({
        name: meal,
      }));
      setValue("selectedMeals", mealNames);
    }
  }, [gotoPreference, isLoading]);

  const onSubmit = (data: GoToFoodFormData) => {
    const mealIdsToSave = data.selectedMeals.map((meal) => meal.name);

    const dataBody: GoToMealsRequest = {
      meals: mealIdsToSave,
    };

    generateCandidate(dataBody, {
      onSuccess: () => {
        reset({
          selectedMeals: [],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GO_TO_PREFERENCE, mealTime],
        });

        onSuccess();
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  const handleFormSubmit = handleSubmit(onSubmit, (errors) => {
    console.log("Form validation errors:", errors);
    if (errors.selectedMeals) {
      showError(
        errors.selectedMeals.message || "Please check your meal selection",
      );
    }
  });

  return {
    form,
    handleFormSubmit,
    isGenerating,
    isValid,
    isLoading,
  };
}
